import React, { useCallback, useEffect, useRef, useState } from 'react';
import { debounce, isEmpty } from 'lodash';

import { faCheckCircle, faRectangleHistoryCirclePlus, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchRethinkLists } from 'bundles/connect/helpers/prospect_api';
import {
  fetchOwners,
  addPropertyToRethinkList,
  fetchRemainingCredits,
  offMarketPropertySearch,
  bulkInsertRethinkCallList
} from '../../../helpers/prospect_api';
import {
  Button,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  Overlay,
  OverlayTrigger,
  Popover,
  Tooltip
} from 'react-bootstrap-5';
import IncludeOwnerDataCheckbox from './include_owner_data_checkbox';
import CreateRethinkList from './create_rethink_list';
import type { Criteria, PropertyCrmSync, RethinkList } from 'bundles/connect/types';
import PropertyCrmSyncModalExporting from './modal_exporting';
import PropertySyncBatchCompleteModal from './property_sync_batch_complete_modal';

type AddToRethinkListProps = {
  asIcon?: boolean;
  bulk?: boolean;
  criteria?: Criteria;
  selectAll?: boolean;
  itemIds?: Set<string>;
  itemCount: number;
  insightsPropertyId?: string;
}

export default function AddToRethinkList({ asIcon = false,
  bulk = false,
  criteria,
  selectAll = false,
  itemIds = new Set<string>(),
  itemCount,
  insightsPropertyId = '' }: AddToRethinkListProps) {
  const [showOverlay, setShowOverlay] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [rethinkLists, setRethinkLists] = useState<RethinkList[]>([]);
  const [savedTo, setSavedTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedList, setSelectedList] = useState<RethinkList | undefined>(undefined);
  const [saveContact, setSaveContact] = useState(false);
  const [cannotIncludeContacts, setCannotIncludeContacts] = useState(false);
  const [email, setEmail] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [rethinkSync, setRethinkSync] = useState<PropertyCrmSync | undefined>(undefined);
  const [fetchedCredits, setFetchedCredits] = useState(0);

  const triggerRef = useRef(null);
  const overlayId = Math.floor(Math.random() * 100000);
  const OWNER_CHECKBOX_PROPS = {
    cannotIncludeContacts: cannotIncludeContacts,
    checked: saveContact,
    disabled: cannotIncludeContacts,
    onChange: () => setSaveContact(!saveContact),
  };

  const debouncedSearch = useCallback(debounce((search) => {
    fetchRethinkLists({ name: search }).then(res => setRethinkLists(res));
    setSelectedList(undefined);
  }, 300), []);

  const assembleCallListItems = async () => {
    let ids = [...itemIds];

    if (selectAll && criteria) {
      const results = await offMarketPropertySearch(criteria, 1000);
      ids = (results.hits || []).map(hit => hit.propertyId);
    }

    return ids;
  };

  const handleOpenOverlay = () => {
    setShowOverlay(true);
    // we need to get remaining credits or owners to disable the checkbox
    fetchRemainingCredits().then((res) => {
      setFetchedCredits(res.remainingCredits);
    })
      .catch(() => {
        setCannotIncludeContacts(true);
      });
  };

  const handleCloseOverlay = () => {
    setShowOverlay(false);
    setShowCreateModal(false);
    setRethinkSync(undefined);
    setSaveContact(false);
    setSearchText('');
    setSelectedList(undefined);
    setSendEmail(false);
    setEmail('');
  };

  const handleEmailCheck = () => {
    setSendEmail(!sendEmail);
    setEmail('');
  };

  const handleBulkInsert = async (rethinkListId: string | undefined,
    email: string,
    sendEmail: boolean,
    listName: string
  ) => {
    const propertyIds = await assembleCallListItems();

    return await bulkInsertRethinkCallList({
      rethink_list_id: rethinkListId,
      insights_property_ids: propertyIds,
      include_contacts: saveContact,
      email: email,
      send_finished_email: sendEmail,
      rethink_list_name_override: listName
    }).then((res) => {
      setShowOverlay(false);
      setRethinkSync(res);
      setShowCreateModal(true);
      setSaving(false);
    });
  };

  const handleEnterPressed = useCallback((e) => {
    e.preventDefault();
    if (searchText.length > 0 && rethinkLists.length === 0) handleShowCreateModal(e);
  }, [searchText, rethinkLists]);

  const handleAddToList = async () => {
    setShowOverlay(true);
    setSaving(true);

    if (bulk) {
      handleBulkInsert(selectedList?.id, email, sendEmail, selectedList?.name || '');
      return;
    }

    if (saveContact) {
      // we want to unlock owners before going to rethink_sync_controller
      await fetchOwners(insightsPropertyId, true, false);
    }

    await addPropertyToRethinkList({
      insightsPropertyId: insightsPropertyId,
      includeContacts: saveContact,
      rethinkListId: selectedList?.id
    }).then(() => {
      setSavedTo(selectedList?.name || '');
      setSaving(false);
    }).catch(() => {
      setSavedTo('');
      setSaving(false);
    });
  };

  const handleShowCreateModal = (e) => {
    e.stopPropagation();

    handleCloseOverlay();
    setShowCreateModal(true);
  };

  useEffect(() => {
    if (showOverlay) {
      setSavedTo('');
      setSaving(false);
      setSearchText('');
    }
  }, [showOverlay]);

  useEffect(() => {
    if (savedTo.length > 0) {
      setTimeout(() => handleCloseOverlay(), 3000);
    }
  }, [savedTo]);

  useEffect(() => {
    if (showOverlay) debouncedSearch(searchText);
  }, [showOverlay, searchText]);

  useEffect(() => {
    if (fetchedCredits < itemCount || fetchedCredits <= 0) {
      setCannotIncludeContacts(true);
    } else {
      setCannotIncludeContacts(false);
    }
  }, [itemCount, fetchedCredits]);

  const popoverTitle = (
    <h6 className="px-3 py-2 m-0 border-bottom">
      Add To List
    </h6>
  );


  const popover = (
    <Popover id={`add-to-list-popover-${overlayId}`} style={{ maxWidth: '500px', minWidth: '320px' }}>
      {saving && (
        <Popover.Body>
          {popoverTitle}
          <div className="text-primary text-center animate__animated animate__fadeIn" style={{ fontSize: '1rem' }}>
            Saving...
          </div>
        </Popover.Body>
      )}
      {!saving && savedTo && (
        <Popover.Body>
          {popoverTitle}
          <div className="text-primary text-center animate__animated animate__fadeIn" style={{ fontSize: '1rem' }}>
            <FontAwesomeIcon className="me-2" icon={faCheckCircle} />
            Saved to "{savedTo}"
          </div>
        </Popover.Body>
      )}
      {!saving && !savedTo && (
        <>
          <Popover.Body className="px-0 pt-2 pb-0">
            {popoverTitle}
            <Form onSubmit={handleEnterPressed}>
              <Form.Group className="mb-2 px-3 pt-3">
                <InputGroup className="flex-nowrap">
                  <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                  <Form.Control autoFocus
                    placeholder="Search by List Name"
                    type="text"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                  />
                </InputGroup>
              </Form.Group>
            </Form>

            {isEmpty(rethinkLists) ? (
              <div className="py-2 px-3">No results found</div>
            ) : (
              <>
                <ListGroup
                  className="mx-3 my-2 border rounded rethink-list-group"
                  variant="flush"
                >
                  {
                    rethinkLists.map(rethinkList => (
                      <ListGroup.Item
                        action
                        active={selectedList?.id === rethinkList.id}
                        className="border-bottom-1"
                        key={rethinkList.id}
                        onClick={() => setSelectedList(rethinkList)}
                      >
                        <div className="w-100 text-truncate">
                          {rethinkList.name}
                        </div>
                      </ListGroup.Item>
                    ))
                  }
                </ListGroup>
                <div className="mx-2 ms-3">
                  <IncludeOwnerDataCheckbox {...OWNER_CHECKBOX_PROPS} />
                </div>
                {bulk &&
                  (<div className="mx-2">
                    <Form.Check
                      checked={sendEmail}
                      id="send-email-rethink-list"
                      label="Email me when list creation is complete"
                      type="checkbox"
                      onChange={handleEmailCheck}
                    />
                    {sendEmail && (
                      <div className="m-2">
                        <Form.Label className="mt-2">Email address</Form.Label>
                        <Form.Control
                          className="mt-2"
                          placeholder="Email address"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                  )}
              </>
            )}
          </Popover.Body>
          <div className="border-top d-flex align-items-center justify-content-between p-2">
            <Button variant="link" onClick={handleShowCreateModal}>
              Create List
            </Button>
            <Button disabled={!selectedList} variant="secondary" onClick={handleAddToList}>
              Add to List
            </Button>
          </div>
        </>
      )}
    </Popover>
  );

  const trigger = (
    <div
      className={`align-content-center text-center h-100 ${bulk ? '' : 'px-2'}`}
      ref={triggerRef}
      onClick={handleOpenOverlay}
    >
      {asIcon ?
        (
          <FontAwesomeIcon
            className="text-primary clickable"
            icon={faRectangleHistoryCirclePlus}
          />
        ) :
        (
          <Button variant="text-primary ms-auto">
            <FontAwesomeIcon className="text-primary clickable" icon={faRectangleHistoryCirclePlus} />
            Add to List
          </Button>
        )}
    </div>
  );

  const renderTooltip = (
    <Tooltip id={`add-to-list-tooltip-${overlayId}`}>
      Add to List
    </Tooltip>
  );

  return (
    <>
      <Modal centered show={showCreateModal} onHide={handleCloseOverlay}>
        {!rethinkSync && (
          <CreateRethinkList
            bulk={bulk}
            insightsPropertyId={insightsPropertyId}
            ownerCheckboxProps={OWNER_CHECKBOX_PROPS}
            onBulkInsert={handleBulkInsert}
            onHide={handleCloseOverlay}
          />
        )}
        {(rethinkSync && rethinkSync.syncProgress < 100) && (
          <PropertyCrmSyncModalExporting
            propertyCrmSync={rethinkSync}
            willContact={rethinkSync.sendFinishedEmail}
            onHide={handleCloseOverlay}
            onSyncComplete={() => setRethinkSync({ ...rethinkSync, syncProgress: 100 })}
          />
        )}
        {(rethinkSync && rethinkSync.syncProgress === 100) && (
          <PropertySyncBatchCompleteModal
            saveContact={saveContact}
            onHide={handleCloseOverlay}
          />
        )}
      </Modal>

      <Overlay
        flip={true}
        placement="auto"
        rootClose
        show={showOverlay}
        target={triggerRef.current}
        onHide={handleCloseOverlay}
      >
        {popover}
      </Overlay>

      <OverlayTrigger
        overlay={asIcon ? renderTooltip : <></>}
        placement="right"
        popperConfig={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            }
          ]
        }}
      >
        {trigger}
      </OverlayTrigger>
    </>
  );
}
